import { useEffect, useState } from "react";
import {
    getMisNotificaciones,
    exportarRespaldoNotificaciones,
    marcarNotificacionComoLeida,
} from "../../../services/notificacion.service";
import { paginacion } from "../../../utils/paginacion/paginacion";
import { ShowButtonsPaginacion } from "../../../utils/paginacion/ShowButtonsPaginacion";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";

function normalizarTexto(texto) {
  return texto
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

function resaltarCoincidencia(textoOriginal, filtro) {
  if (!filtro) return textoOriginal;
  const texto = normalizarTexto(textoOriginal);
  const normalFiltro = normalizarTexto(filtro);
  const partes = texto.split(new RegExp(`(${normalFiltro})`, "gi"));

  let posicion = 0;
  return partes.map((parte, index) => {
    const slice = textoOriginal.slice(posicion, posicion + parte.length);
    posicion += parte.length;
    return normalizarTexto(parte) === normalFiltro ? (
      <mark key={index} className="bg-yellow-200 text-black rounded px-1">
        {slice}
      </mark>
    ) : (
      <span key={index}>{slice}</span>
    );
  });
}

function formatearFecha(fecha) {
  const date = new Date(fecha);
  const dia = String(date.getDate()).padStart(2, '0');
  const mes = String(date.getMonth() + 1).padStart(2, '0');
  const año = date.getFullYear();
  const horas = String(date.getHours()).padStart(2, '0');
  const minutos = String(date.getMinutes()).padStart(2, '0');
  const segundos = String(date.getSeconds()).padStart(2, '0');
  return `${dia}/${mes}/${año}, ${horas}:${minutos}:${segundos}`;
}

export const NotificacionesPage = () => {
    const [notificaciones, setNotificaciones] = useState([]);
    const [paginaActual, setPaginaActual] = useState(1);
    const [filtro, setFiltro] = useState("");
    const navigate = useNavigate();

    const cargarNotificaciones = async () => {
        try {
            const data = await getMisNotificaciones();
            setNotificaciones(data);
        } catch (error) {
            toast.error("Error al cargar notificaciones");
        }
    };

    useEffect(() => {
        cargarNotificaciones();
    }, []);

    const handleExportarExcel = async () => {
        try {
            await exportarRespaldoNotificaciones();
        } catch (error) {
            toast.error("Error al exportar archivo Excel");
        }
    };

    const handleMarcarTodasComoLeidas = async () => {
        try {
            const promesas = notificaciones
                .filter((n) => !n.leido)
                .map((n) => marcarNotificacionComoLeida(n.id));
            await Promise.all(promesas);
            toast.success("Todas las notificaciones marcadas como leídas");
            cargarNotificaciones();
        } catch (error) {
            toast.error("Error al marcar como leídas");
        }
    };

    const handleMarcarUna = async (id) => {
        try {
            await marcarNotificacionComoLeida(id);
            toast.success("Notificación marcada como leída");
            cargarNotificaciones();
        } catch (error) {
            toast.error("Error al marcar notificación");
        }
    };

    const notificacionesFiltradas = notificaciones.filter((n) => {
        const mensaje = normalizarTexto(n.mensaje || "");
        const textoFiltro = normalizarTexto(filtro);
        return mensaje.includes(textoFiltro);
    });

    const { objetosEnPagina, paginas, paginaInicial, paginaFinal } = paginacion(
        paginaActual,
        notificacionesFiltradas,
        6
    );

    return (
        <div className="bg-gradient-to-b from-white via-gray-50 to-gray-100 min-h-screen">
            <div className="p-6 max-w-6xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-center mb-6 border-b pb-4 border-gray-300">
                    <h2 className="text-3xl font-bold text-gray-700 flex items-center gap-2">
                        <span className="text-rose-600">📢</span> Todas las notificaciones
                    </h2>
                    <div className="flex flex-wrap gap-2 mt-4 md:mt-0">
                        <button
                            onClick={handleExportarExcel}
                            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 shadow"
                        >
                            Descargar Excel
                        </button>
                        <button
                            onClick={handleMarcarTodasComoLeidas}
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 shadow"
                        >
                            Marcar todas como leídas
                        </button>
                        <button
                            onClick={cargarNotificaciones}
                            className="bg-white border border-blue-500 text-blue-600 px-4 py-2 rounded hover:bg-blue-50 shadow"
                        >
                            🔄 Recargar
                        </button>
                    </div>
                </div>

                <div className="relative w-full mb-4">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    🔍
                  </span>
                  <input
                    type="text"
                    placeholder="Buscar por nombre, apellido o grupo..."
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value)}
                    className="pl-10 w-full px-4 py-2 border border-gray-300 rounded shadow-sm focus:outline-none focus:ring focus:border-blue-300"
                  />
                </div>

                <div className="bg-white shadow-lg rounded-lg overflow-hidden divide-y divide-gray-200">
                    {objetosEnPagina.map((n) => (
                        <div
                            key={n.id}
                            className={`flex flex-col md:flex-row md:items-center justify-between gap-2 px-5 py-4 transition-all duration-300 ${n.leido ? "bg-white text-gray-600" : "bg-blue-100 text-gray-800"}`}
                        >
                            <div className="flex items-start gap-3 flex-1">
                                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 cursor-pointer" onClick={() => {
                                    if (n.idEstudiante) navigate(`/estudiantes/actualizar/${n.idEstudiante}`);
                                }}>
                                    <p className="text-sm font-medium">{resaltarCoincidencia(n.mensaje, filtro)}</p>
                                    <p className="text-xs text-gray-400 mt-1">{formatearFecha(n.fecha)}</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2 md:ml-4">
                                {!n.leido && (
                                    <button
                                        onClick={() => handleMarcarUna(n.id)}
                                        className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-sm"
                                    >
                                        Marcar como leída
                                    </button>
                                )}
                                {n.idEstudiante && (
                                    <button
                                        onClick={() => navigate(`/estudiantes/actualizar/${n.idEstudiante}`)}
                                        className="bg-emerald-600 text-white px-3 py-1 rounded hover:bg-emerald-700 text-sm"
                                    >
                                        Actualizar estudiante
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-6">
                    <ShowButtonsPaginacion
                        paginaInicial={paginaInicial}
                        paginaFinal={paginaFinal}
                        paginaActual={paginaActual}
                        setPaginaActual={setPaginaActual}
                        paginas={paginas}
                    />
                </div>
            </div>
        </div>
    );
};
